import { FormSelect, Option, cleanArray, theme } from '@arnold/common';
import { Field, FormikProps } from 'formik';
import { Col, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { OrganizationTeamsQuery } from '../../../generated/hooks';
import ee from '../../../lib/eventEmitter';
import { FormGroupLabel } from '../../Common';
import { useOrganization } from '../../providers/OrganizationProvider';
import { useCallback } from 'react';
import styled from '@emotion/styled/macro';

export const NO_SUPERVISOR = 'noSupervisor';

type Props = {
  teams: NonNullable<OrganizationTeamsQuery['organizationTeams']>;
  disableCreating?: boolean;
  disableNoSupervisor?: boolean;
  onChange?: (option: Option) => void;
  id?: string;
  error?: string | false;
  className?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  createTeamOption?: boolean;
  onCreateTeamSelected?: (externallySetTeamId: (teamId: string) => void) => void;
} & Partial<FormikProps<any>>;

export function TeamLeaderField({
  teams,
  onChange,
  disableCreating,
  values,
  setFieldValue,
  disableNoSupervisor,
  id,
  error,
  className,
  fullWidth,
  disabled,
  createTeamOption,
  onCreateTeamSelected,
  ...props
}: Props) {
  const { t } = useTranslation('updateRespondent');
  const { refetch, organization } = useOrganization();

  const externallySetTeamId = useCallback(
    (teamId: string) => {
      setFieldValue?.('teamId', teamId);
    },
    [setFieldValue],
  );

  const handleChange = (option: Option) => {
    if (option.value === 'newLeader') {
      ee.emitNewTeam((teamId) => {
        refetch().then(() => {
          setFieldValue?.('teamId', teamId);
        });
      }, organization);
    } else if (option.value === 'createTeam') {
      onCreateTeamSelected?.(externallySetTeamId);
    } else {
      onChange?.(option);
    }
  };

  const options = cleanArray([
    createTeamOption && {
      label: t('createTeam'),
      value: 'createTeam',
    },
    !disableCreating && {
      label: t('newLeader'),
      value: 'newLeader',
      // means field will not send event to form about changing value
      handling: 'custom',
    },
    !disableNoSupervisor && {
      value: NO_SUPERVISOR,
      label: t('teams:withoutLeader'),
    },
    ...teams.map((team) => ({
      value: team.id,
      label: `${t('team')}: ${team.name} ${
        team.leader ? `- ${t('leads')} ${team.leader.firstname} ${team.leader.surname}` : ''
      }`, // getRespondentNameLabel(team.leader, team.name!, t),
      additionalInfo: {
        repondentId: team.leader?.id,
      },
    })),
  ]);
  const val = values?.teamId === null ? NO_SUPERVISOR : values?.teamId;
  return (
    <FormGroup as={Col} sm={fullWidth ? 12 : 8} className={className}>
      <FormGroupLabel>{t('team')}</FormGroupLabel>
      <TeamLeaderWrapper hasCreateOption={!!createTeamOption}>
        <Field
          component={FormSelect}
          name="teamId"
          options={options}
          isSearchable
          {...props}
          onChange={handleChange}
          placeholder={t('search')}
          isInvalid={!!error}
          value={val}
          id={id || 'team-leader-select'}
          disabled={disabled}
        />
      </TeamLeaderWrapper>
      {error && <span className="form-text text-danger mr-6 w-auto">{error}</span>}
    </FormGroup>
  );
}

const TeamLeaderWrapper = styled.div<{ hasCreateOption: boolean }>`
  ${({ hasCreateOption }) =>
    hasCreateOption &&
    `
  [class*='MenuList'] > div:first-child {
  position: relative;
  display: flex;
  align-items: center;
  border-bottom: none;
}

[class*='MenuList'] > div:first-child::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 3.5%;
  right: 3.5%;
  width: auto;
  height: 1px;
  background-color: ${theme.colors.borderMain.default};
}

  [class*='MenuList'] > div:first-child:before {
    content: '+';
    margin-right: 8px;
    color: ${theme.colors.iconText.primary};
    font-size: 22px;
    height: 22px;
    display: flex;
    align-items: center;
  }
  `}
`;
